import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { ChartErrorBoundary } from './ChartErrorBoundary';
import { ChartHeader } from './ChartHeader';
import { ChartNoData } from './ChartNoData';
import { ChartProvider, useChartState } from './ChartContext';

interface ChartContainerProps {
    children: ReactNode;
    onRemove?: () => void;
    isEditMode?: boolean;
    isLoading?: boolean;
    isError?: boolean;
    className?: string;
    chartName?: string;
    title?: string;
    description?: string;
    summary?: React.ReactNode;
    headerActions?: React.ReactNode;
    isEmpty?: boolean;
    emptyTitle?: string;
    emptyDescription?: string;
    onClick?: (e: React.MouseEvent) => void;
}

/**
 * ChartContainer - Standard container for all board charts
 * Provides consistent layout and interaction features
 */
export const ChartContainer: React.FC<ChartContainerProps> = ({
    children,
    ...props
}) => {
    return (
        <ChartProvider>
            <ChartContainerInner {...props}>
                {children}
            </ChartContainerInner>
        </ChartProvider>
    );
};

const ChartContainerInner: React.FC<ChartContainerProps> = ({
    children,
    onRemove,
    isEditMode = false,
    isLoading = false,
    isError = false,
    className = '',
    chartName,
    title: propsTitle,
    description: propsDescription,
    summary: propsSummary,
    headerActions: propsHeaderActions,
    isEmpty: propsIsEmpty,
    emptyTitle,
    emptyDescription,
    ...props
}) => {
    const {
        title: ctxTitle,
        description: ctxDescription,
        summary: ctxSummary,
        isEmpty: ctxIsEmpty,
        headerActions: ctxHeaderActions,
        emptyTitle: ctxEmptyTitle,
        emptyDescription: ctxEmptyDescription
    } = useChartState();

    // Context values override props
    const title = ctxTitle ?? propsTitle;
    const description = ctxDescription ?? propsDescription;
    const summary = ctxSummary ?? propsSummary;
    const isEmpty = ctxIsEmpty || propsIsEmpty;
    const headerActions = ctxHeaderActions ?? propsHeaderActions;
    const finalEmptyTitle = ctxEmptyTitle ?? emptyTitle;
    const finalEmptyDescription = ctxEmptyDescription ?? emptyDescription;


    const containerRef = useRef<HTMLDivElement>(null);
    const [containerKey, setContainerKey] = useState(0);
    const [sizeTier, setSizeTier] = useState<'tiny' | 'small' | 'medium' | 'large'>('large');
    const prevSize = useRef({ width: 0, height: 0 });

    useEffect(() => {
        let resizeTimer: NodeJS.Timeout;
        const handleResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                setContainerKey(prev => prev + 1);
            }, 150);
        };

        window.addEventListener('resize', handleResize);
        const resizeObserver = new ResizeObserver((entries) => {
            if (!entries || !entries[0]) return;
            const { width, height } = entries[0].contentRect;
            if (width === prevSize.current.width && height === prevSize.current.height) return;

            prevSize.current = { width, height };

            // Determine size tier
            let tier: 'tiny' | 'small' | 'medium' | 'large' = 'large';
            if (height < 120 || width < 160) tier = 'tiny';
            else if (height < 200 || width < 300) tier = 'small';
            else if (height < 350 || width < 500) tier = 'medium';
            
            setSizeTier(tier);

            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                setContainerKey(prev => prev + 1);
            }, 150);

        });

        if (containerRef.current) resizeObserver.observe(containerRef.current);

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserver.disconnect();
            clearTimeout(resizeTimer);
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className={`group relative bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col h-full w-full ${isEditMode ? 'ring-2 ring-transparent hover:ring-blue-400/30' : ''
                } ${className}`}
        >
            {/* Standard Header */}
            {title && (
                <ChartHeader
                    title={title}
                    description={description}
                    summary={summary}
                    actions={headerActions}
                    onRemove={onRemove}
                    isEditMode={isEditMode}
                />
            )}

            {/* Chart Content with Error Boundary */}
            <div
                key={containerKey}
                className="flex-1 min-h-0 min-w-0 relative overflow-hidden"
            >
                {isEmpty && !isLoading && !isError ? (
                    <ChartNoData 
                        title={finalEmptyTitle} 
                        description={finalEmptyDescription} 
                        size={sizeTier}
                    />
                ) : (
                    <div className="h-full w-full">
                        <ChartErrorBoundary chartName={chartName}>
                            {children}
                        </ChartErrorBoundary>
                    </div>
                )}

                {/* Chart Error Overlay */}
                {isError && (
                    <div
                        onClick={(e) => {
                            if (props.onClick) props.onClick(e);
                        }}
                        className="absolute inset-0 z-10 rounded-lg overflow-hidden flex flex-col items-center justify-center bg-white/50 dark:bg-neutral-900/50 backdrop-blur-[2px] cursor-pointer hover:bg-white/60 dark:hover:bg-neutral-800/60 transition-colors"
                    >
                        <div className="w-12 h-12 bg-rose-50 dark:bg-rose-950/30 rounded-full flex items-center justify-center mb-3 shadow-sm border border-rose-100 dark:border-rose-900">
                            <span className="w-6 h-6 text-rose-500" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_e39b0d1c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_e39b0d1c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
